import React from "react";
import TasksTab from "./TasksTab";
import JobAllocationTab from "./JobAllocationTab";
import QueueingTab from "./QueueingTab";
import SaveScenarioTab from "./SaveScenarioTab";
import CompareScenarioTab from "./CompareScenarioTab";
import useWhatIfQueueing from "../../hooks/useWhatIfQueueing";
import useCompanyJobs from "../../hooks/useCompanyJobs";
import { convertCurrency } from "../../services/currencyService";
import { PARTICIPANT_CURRENCY } from "../../utils/whatif/whatIfNormalize";
import {
  SCENARIO_ACTIONS,
  computeScenario,
  createScenarioDraft,
  scenarioReducer,
} from "../../utils/whatif/whatIfScenario";
import {
  formatAmount,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";

// The three views of a scenario: what the tasks themselves cost in time, who is
// staffed against them, and whether that staffing can absorb the demand. They
// share one draft, so eliminating a task in the first tab is reflected in the
// other two.
const TABS = [
  { key: "tasks", label: "Tasks" },
  { key: "jobs", label: "Job Allocation" },
  { key: "queueing", label: "Queuing Analysis" },
  { key: "save", label: "Save Scenario" },
  { key: "compare", label: "Compare Scenario" },
];

// One row of the impact table: label, as-is, to-be, delta — real columns
// instead of an inline-wrapped pill, so the three rows line up and are
// scannable rather than reading as one run-on line.
const ImpactRow = ({ label, current, original, render }) => {
  const delta = current - original;
  const percent = original !== 0 ? (delta / original) * 100 : 0;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div className="wif-impact-row">
      <div className="wif-impact-label">{label}</div>
      <div className="wif-impact-cell">{render(original)}</div>
      <div className="wif-impact-cell wif-impact-cell--strong">
        {render(current)}
      </div>
      <div className="wif-impact-cell">
        <span className={`wif-delta wif-delta--${direction}`}>
          {delta >= 0 ? "+" : "−"}
          {render(Math.abs(delta))}
          {original !== 0 && ` (${formatPercent(percent, 1)})`}
        </span>
      </div>
    </div>
  );
};

const ScenarioEditor = ({
  tasks,
  metrics,
  processId,
  companyId,
  timeUnit,
  currency,
  rates,
  onClose,
}) => {
  const [draft, dispatch] = React.useReducer(
    scenarioReducer,
    tasks,
    createScenarioDraft,
  );
  const [tab, setTab] = React.useState(TABS[0].key);
  const queueing = useWhatIfQueueing(metrics?.caseArrivalPerWeek);
  // Destructured, not held as one object: the hook returns a fresh literal each
  // render, so depending on it would defeat every useCallback below it.
  const {
    jobsById: companyJobsById,
    error: companyJobsError,
    resolveJobDetails,
  } = useCompanyJobs(companyId);

  const scenario = React.useMemo(() => computeScenario(draft), [draft]);

  // Participant costs are USD throughout the view model, but a company job is
  // quoted in whatever currency it was recorded in, so it is converted on the
  // way in rather than at render time like the display currency.
  const toUsdRate = React.useCallback(
    (job) =>
      job?.hourlyRate == null
        ? null
        : convertCurrency(
            job.hourlyRate,
            job.currencyType || PARTICIPANT_CURRENCY,
            PARTICIPANT_CURRENCY,
            rates,
          ),
    [rates],
  );

  const handleJobChange = React.useCallback(
    (taskId, slotId, job) => {
      // Apply immediately: waiting on a request before the picker updates would
      // make every reassignment feel broken.
      const known = job.hourlyRateUsd ?? toUsdRate(job);
      dispatch({
        type: SCENARIO_ACTIONS.setJob,
        taskId,
        slotId,
        job: { ...job, hourlyRateUsd: known },
      });

      // Only a job the payload never priced needs looking up.
      if (known != null && job.staffCount != null) return;

      (async () => {
        const details = await resolveJobDetails(job.jobId);
        if (!details) return;
        dispatch({
          type: SCENARIO_ACTIONS.setJobRate,
          taskId,
          slotId,
          jobId: job.jobId,
          hourlyRateUsd: known ?? toUsdRate(details),
          staffCount: details.staffCount,
        });
      })();
    },
    [resolveJobDetails, toUsdRate],
  );

  const handleAllocationChange = React.useCallback(
    (taskId, slotId, allocationPercent) => {
      dispatch({
        type: SCENARIO_ACTIONS.setAllocation,
        taskId,
        slotId,
        allocationPercent,
      });
    },
    [],
  );

  const handleResetTask = React.useCallback((taskId) => {
    dispatch({ type: SCENARIO_ACTIONS.resetTask, taskId });
  }, []);

  const handleAddSlot = React.useCallback((taskId, role) => {
    dispatch({ type: SCENARIO_ACTIONS.addSlot, taskId, role });
  }, []);

  const handleRemoveSlot = React.useCallback((taskId, slotId) => {
    dispatch({ type: SCENARIO_ACTIONS.removeSlot, taskId, slotId });
  }, []);

  const handleTimeChange = React.useCallback((taskId, component, minutes) => {
    dispatch({ type: SCENARIO_ACTIONS.setTaskTime, taskId, component, minutes });
  }, []);

  const handleClassChange = React.useCallback((taskId, classification) => {
    dispatch({
      type: SCENARIO_ACTIONS.setClassification,
      taskId,
      classification,
    });
  }, []);

  const money = (amountUsd) =>
    formatAmount(amountUsd, PARTICIPANT_CURRENCY, currency, rates);
  const duration = (minutes) => formatDurationShort(minutes, timeUnit);

  // Anchor-and-adjust, not a resum of the flat task rows: Cycle Time is the
  // process's true end-to-end duration (processing + waiting + rework across
  // every task, subprocesses included), and the scenario editor can't
  // recompute that from scratch — subprocesses aren't in the flat task list
  // at all, and for some processes even the flat rows don't carry per-task
  // waiting/rework figures. Subprocess duration never changes during a
  // session (nothing here edits it), so it's safe to hold the backend's true
  // baseline fixed and add only the delta this session's task edits and
  // eliminations caused — measured from the flat, editable rows, which is
  // exactly the right scope for a delta even though it's the wrong scope for
  // an absolute total.
  const cycleTimeAsIs = metrics?.cycleTimeMinutes ?? 0;
  const cycleTimeToBe =
    cycleTimeAsIs + (scenario.taskMinutes - scenario.originalTaskMinutes);

  // Anchored to the backend's own to-be figure, not derived from the staffing
  // total: labor cost per case and the RACI matrix's total staffing cost do not
  // move by the same proportion (verified against a live payload — a 7.4%
  // staffing-cost change accompanied a 27.5% labor-cost-per-case change), so
  // scaling one by the other's ratio fabricates a number the backend never
  // reported. Anchoring to `laborCostPerCaseToBe` reproduces the backend's
  // exact figure the moment the editor opens — `scenario.costUsd` equals
  // `scenario.originalCostUsd` at load, giving a ratio of 1 — and only moves
  // further for edits made after that.
  const laborCostPerCase = metrics?.laborCostPerCase ?? 0;
  const laborCostPerCaseToBeBase =
    metrics?.laborCostPerCaseToBe ?? laborCostPerCase;
  const sessionRatio =
    scenario.originalCostUsd > 0
      ? scenario.costUsd / scenario.originalCostUsd
      : 1;
  const laborCostPerCaseToBe = laborCostPerCaseToBeBase * sessionRatio;

  return (
    <div className="role-card">
      {/* Head and totals travel together as one sticky strip: the task list runs
          long, and the whole point of the numbers is to watch them move while
          editing rows further down. */}
      <div className="wif-scenario-sticky">
        <div className="wif-scenario-head">
          <div className="section-title">Scenario</div>
          <div className="wif-scenario-actions">
            <button
              type="button"
              className="secondary-btn"
              disabled={!scenario.changedCount}
              onClick={() => dispatch({ type: SCENARIO_ACTIONS.resetAll })}
            >
              Reset all
            </button>
            <button type="button" className="secondary-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="wif-tabs" role="tablist">
          {TABS.map((entry) => (
            <button
              key={entry.key}
              type="button"
              role="tab"
              aria-selected={tab === entry.key}
              className={`wif-tab${tab === entry.key ? " is-active" : ""}`}
              onClick={() => setTab(entry.key)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {/* Both totals stay pinned on both tabs: a duration edit moves time, a
            reassignment moves cost, and an elimination moves both. */}
        <div className="wif-impact-table">
          <div className="wif-impact-row wif-impact-head">
            <div className="wif-impact-label">Impact</div>
            <div className="wif-impact-cell">As-Is</div>
            <div className="wif-impact-cell">To-Be</div>
            <div className="wif-impact-cell">Change</div>
          </div>
          <ImpactRow
            label="Cycle time"
            original={cycleTimeAsIs}
            current={cycleTimeToBe}
            render={duration}
          />
          <ImpactRow
            label="Staffing cost"
            original={scenario.originalGrossCostUsd}
            current={scenario.costUsd}
            render={money}
          />
          <ImpactRow
            label="Labour cost / case"
            original={laborCostPerCase}
            current={laborCostPerCaseToBe}
            render={money}
          />
        </div>
        <div className="wif-impact-counts">
          <span className="wif-impact-count">
            <span className="wif-compare-label">Changes</span>{" "}
            <strong>{scenario.changedCount}</strong>
          </span>
          {scenario.eliminatedCount > 0 && (
            <span className="wif-impact-count">
              <span className="wif-compare-label">Eliminated</span>{" "}
              <strong>{scenario.eliminatedCount}</strong>
            </span>
          )}
        </div>
      </div>

      {(scenario.unassignedCount > 0 || scenario.unratedCount > 0) && (
        <ul className="whatif-warnings">
          {scenario.unassignedCount > 0 && (
            <li>
              {scenario.unassignedCount} added assignment
              {scenario.unassignedCount === 1 ? "" : "s"} still need
              {scenario.unassignedCount === 1 ? "s" : ""} a job selected.
            </li>
          )}
          {scenario.unratedCount > 0 && (
            <li>
              {scenario.unratedCount} assignment
              {scenario.unratedCount === 1 ? " has" : "s have"} no hourly rate on
              record and {scenario.unratedCount === 1 ? "is" : "are"} excluded
              from the cost total.
            </li>
          )}
        </ul>
      )}

      {tab === "tasks" && (
        <TasksTab
          scenario={scenario}
          timeUnit={timeUnit}
          onTimeChange={handleTimeChange}
          onClassChange={handleClassChange}
          onResetTask={handleResetTask}
        />
      )}

      {tab === "jobs" && (
        <JobAllocationTab
          scenario={scenario}
          companyJobsById={companyJobsById}
          companyJobsError={companyJobsError}
          timeUnit={timeUnit}
          currency={currency}
          rates={rates}
          onJobChange={handleJobChange}
          onAllocationChange={handleAllocationChange}
          onAddSlot={handleAddSlot}
          onRemoveSlot={handleRemoveSlot}
          onResetTask={handleResetTask}
        />
      )}

      {tab === "queueing" && (
        <QueueingTab
          scenario={scenario}
          timeUnit={timeUnit}
          queueing={queueing}
        />
      )}

      {tab === "save" && (
        <SaveScenarioTab
          scenario={scenario}
          processId={processId}
          companyId={companyId}
          cycleTimeAsIs={cycleTimeAsIs}
          cycleTimeToBe={cycleTimeToBe}
          laborCostPerCase={laborCostPerCase}
          laborCostPerCaseToBe={laborCostPerCaseToBe}
          timeUnit={timeUnit}
          currency={currency}
          rates={rates}
          queueing={queueing}
        />
      )}

      {tab === "compare" && (
        <CompareScenarioTab
          processId={processId}
          companyId={companyId}
          timeUnit={timeUnit}
          currency={currency}
          rates={rates}
        />
      )}
    </div>
  );
};

export default ScenarioEditor;
